import React, { useCallback, useContext, useEffect, useState } from 'react';
import { useSelector, useDispatch } from 'react-redux';
import { useTranslation } from 'react-i18next';
import { Form } from 'react-final-form';
import { Link as RouterLink, useParams, useNavigate } from 'react-router-dom';
import LegalFields from 'components/legal/LegalFields';
import { ContentPreviewAction } from 'components/shared/content/ContentPreviewDialog';
import PrimaryLoadingButton from 'components/shared/buttons/PrimaryLoadingButton';
import SecondaryButtonLoading from 'components/shared/buttons/SecondaryLoadingButton';
import SEOPageTitle from 'components/shared/SEOPageTitle';
import defaultLegalFormValidations from 'common/validations/form/defaultLegal';
import routes from 'common/routes';
import requestStatus from 'common/constants/status';
import { updateLegalInitialValues } from 'common/form/initial-values/update';
import getDataToSave from 'common/form/dataToSave';
import { getFieldsToUpdate } from 'common/form/utils';
import StyledButtonsContainer from 'common/theme/containers/StyledButtonsContainer';
import AppBarContext from 'context/AppBarContext';
import legalSlice from 'store/modules/legal';

const requiredFields = ['created_at', 'body', 'title'];

function LegalEdit() {
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const { t } = useTranslation(['routes']);
  const { id } = useParams();
  const pageTitle = t('legal.edit', { ns: 'routes' });
  const [initialValues, setInitialValues] = useState({});
  const { setAppBarButton, setAppBarTitle } = useContext(AppBarContext);

  const legal = useSelector((state) => state.legal.data.currentLegal);
  const status = useSelector((state) => state.legal.status);
  const previousStatus = React.useRef(status);
  // `find` y `update` comparten `state.legal.status`; sin este guard el loading→success
  // del find inicial dispararía la redirección y rebotaría a la lista sin mostrar los datos.
  const hasSubmitted = React.useRef(false);

  useEffect(() => {
    dispatch(legalSlice.actions.find(id));
  }, [dispatch, id]);

  useEffect(() => {
    if (legal && Object.keys(legal).length > 0 && legal.id === id) {
      const legalData = updateLegalInitialValues(legal);
      setInitialValues(legalData);
    }
  }, [legal]);

  useEffect(() => {
    setAppBarButton(null);
    setAppBarTitle(pageTitle);
  }, [pageTitle, setAppBarButton, setAppBarTitle]);

  // Navegar a la lista SOLO después de una edición (submit) exitosa.
  useEffect(() => {
    if (
      hasSubmitted.current &&
      previousStatus.current === requestStatus.loading &&
      status === requestStatus.success
    ) {
      navigate(routes.listLegal.path);
    }
    previousStatus.current = status;
  }, [status, navigate]);

  const onEdit = useCallback(
    (values) => {
      const fieldsToUpdate = getFieldsToUpdate(values, initialValues);
      const dataToSave = getDataToSave(fieldsToUpdate, 'legal');
      hasSubmitted.current = true;
      dispatch(legalSlice.actions.update({ id, input: dataToSave }));
    },
    [dispatch, id, initialValues]
  );

  return (
    <>
      <SEOPageTitle title={pageTitle} />
      <Form
        initialValues={initialValues}
        onSubmit={onEdit}
        validate={defaultLegalFormValidations}
        render={({ handleSubmit, invalid, pristine, values }) => (
          <form onSubmit={handleSubmit}>
            <LegalFields requiredFields={requiredFields} />
            <StyledButtonsContainer>
              <ContentPreviewAction title={values.title} body={values.body} />
              <SecondaryButtonLoading
                component={RouterLink}
                disabled={status === requestStatus.loading}
                style={{ marginRight: '32px' }}
                label={t('cancel', { ns: 'buttons' })}
                to={routes.listLegal.path}
                type="button"
              />
              <PrimaryLoadingButton
                disabled={invalid || pristine || status === requestStatus.loading}
                loading={status === requestStatus.loading}
                label={t('legal.edit', { ns: 'buttons' })}
                type="submit"
              />
            </StyledButtonsContainer>
          </form>
        )}
      />
    </>
  );
}

export default LegalEdit;
